<template>
	<div class="desktop-layout" :class="[colourMode, { 'sidebar-collapsed': sidebarCollapsed }]">
		<div class="desktop-content" :style="{ top: safeTop + 'px' }">
			<button class="menu-toggle" @click="sidebarOpen = !sidebarOpen">
				<v-icon>mdi-menu</v-icon>
			</button>
			<DesktopSidebar
				:open="sidebarOpen"
				:collapsed="sidebarCollapsed"
				:colourMode="colourMode"
				:safeTop="safeTop"
				:navItems="sidebarConfig.navItems || defaultNavItems"
				:pageItems="sidebarPageItems.length ? sidebarPageItems : (sidebarData.recentChats || [])"
				:pageItemsTitle="sidebarConfig.pageItemsTitle"
				:pageItemsEmptyText="sidebarConfig.pageItemsEmptyText"
				:currentPageItemId="sidebarConfig.currentPageItemId || sidebarData.currentChatId"
				:actionButton="sidebarConfig.actionButton"
				:userName="sidebarData.userName || 'User'"
				@close="sidebarOpen = false"
				@logout="handleLogout"
				@action-click="handleActionClick"
				@page-item-click="handlePageItemClick"
				@page-item-delete="handlePageItemDelete"
				@page-item-rename="handlePageItemRename"
				@open-settings="handleOpenSettings"
				@toggle-collapse="toggleSidebarCollapse"
			/>
			<div class="page-container">
				<router-view @colour-mode-changed="colourMode = $event" />
			</div>
		</div>
	</div>
</template>

<script>
import DesktopSidebar from './components/DesktopSidebar.vue';

export default {
	name: 'DesktopLayout',
	components: {
		DesktopSidebar
	},
	provide() {
		return {
			registerSidebar: (config) => {
				this.sidebarConfig = config.config || {};
				this.sidebarData = config.data || {};
				this.sidebarHandlers = config.handlers || {};
				// Reset page items when a new page registers
				this.sidebarPageItems = [];
				if (config.config?.pageItems && config.config.pageItems.length > 0) {
					this.sidebarPageItems = [...config.config.pageItems];
				}
			},
			updateSidebarPageItems: (items) => {
				this.sidebarPageItems = [...items];
			}
		};
	},
	data() {
		return {
			colourMode: 'dark',
			sidebarOpen: false,
			sidebarCollapsed: false,
			sidebarConfig: {},
			sidebarData: {},
			sidebarHandlers: {},
			sidebarPageItems: [],
			defaultNavItems: [
//				{ icon: 'mdi-wallet-outline', label: 'Money', route: '/desktop/money' },
				{ icon: 'mdi-format-list-bulleted', label: 'Money', route: '/desktop/money' },
				{ icon: 'mdi-chart-pie', label: 'Analysis', route: '/desktop/analysis' },
//				{ icon: 'mdi-ticket-percent-outline', label: 'Vouchers', route: '/desktop/shopvouchers' },
				{ icon: 'mdi-chat-outline', label: 'Chat', route: '/desktop/chat' }
			]
		}
	},
	computed: {
		safeTop() {
			return parseInt(this.$cookies.get('st') ?? 0);
		}
	},
	methods: {
		handleLogout() {
			this.$cookies.remove('token');
			this.$router.push({ path: '/login', query: { landingpage: this.$route.path } });
		},
		handleActionClick() {
			this.sidebarHandlers?.onActionClick?.();
			this.sidebarOpen = false;
		},
		handlePageItemClick(id) {
			this.sidebarHandlers?.onPageItemClick?.(id);
			this.sidebarOpen = false;
		},
		handlePageItemDelete(id) {
			this.sidebarHandlers?.onPageItemDelete?.(id);
		},
		handlePageItemRename(data) {
			this.sidebarHandlers?.onPageItemRename?.(data);
		},
		handleOpenSettings() {
			this.sidebarHandlers?.onOpenSettings?.();
			this.sidebarOpen = false;
		},
		toggleSidebarCollapse() {
			this.sidebarCollapsed = !this.sidebarCollapsed;
			localStorage.setItem('mg_sidebar_collapsed', this.sidebarCollapsed);
		}
	},
	mounted() {
		const savedMode = localStorage.getItem('mg_colour_mode');
		if (savedMode) {
			this.colourMode = savedMode;
		} else {
			this.colourMode = 'light';
			localStorage.setItem('mg_colour_mode', 'light');
		}
		const savedCollapsed = localStorage.getItem('mg_sidebar_collapsed');
		if (savedCollapsed === 'true') {
			this.sidebarCollapsed = true;
		}
	}
}
</script>

<style scoped>
.desktop-layout {
	min-height: 100vh;
	background-color: var(--dark-bg-primary);
}

.desktop-layout.light {
	background-color: var(--background-color-main);
}

.desktop-content {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
}

.menu-toggle {
	position: absolute;
	top: 16px;
	left: 16px;
	z-index: 100;
	width: 40px;
	height: 40px;
	background: var(--dark-bg-secondary);
	border: none;
	border-radius: 8px;
	color: var(--dark-text-primary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background-color 0.2s;
}

.menu-toggle:hover {
	background: var(--dark-bg-hover);
}

.desktop-layout.light .menu-toggle {
	background: var(--background-color-alt);
	color: var(--text-color);
}

.desktop-layout.light .menu-toggle:hover {
	background: var(--border-color);
}

.page-container {
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	padding: 0 16px;
	position: relative;
}

@media (min-width: 1024px) {
	.menu-toggle { display: none; }
	.page-container { margin-left: 260px; padding: 0 24px; transition: margin-left 0.3s ease; }
	.sidebar-collapsed .page-container { margin-left: 64px; }
}
</style>

<style>
/* Desktop CSS Variables */
.desktop-layout {
	/* Brand colors */
	--brand-purple: #413655;
	--brand-purple-light: #5a4a70;
	--brand-purple-highlight: #f5f3f7;

	/* Category colors */
	--category-income: #B4D8D7;
	--category-income-hover: #9fcfce;
	--category-future: #D5C9C8;
	--category-everyday: #9EA2D0;
	--category-committed: #413655;

	/* Semantic colors */
	--positive-amount: #23AF64;
	--delete-color: #e57373;

	/* Light mode */
	--light-text-primary: #1a1a1a;
	--light-text-secondary: #666;
	--light-text-muted: #999;
	--light-bg-primary: white;
	--light-bg-secondary: #f8f8f8;
	--light-bg-tertiary: #fafafa;
	--light-bg-hover: #f5f5f5;
	--light-bg-selected: #f0f0f0;
	--light-border: #f0f0f0;
	--light-border-strong: #ddd;
	--light-border-subtle: #eee;

	/* Suggestion banner - light mode */
	--suggestion-bg: #f0f9f8;
	--suggestion-border: #e0f0ef;

	/* Suggestion banner - dark mode */
	--suggestion-bg-dark: rgba(180, 216, 215, 0.1);
	--suggestion-border-dark: rgba(180, 216, 215, 0.2);
}

/* Fixed header for desktop layout */
.desktop-layout .fixed-header .headerBlock {
	position: fixed;
	top: 0;
	left: 0;
}

/* Global styles for desktop layout - prevents white flash */
body:has(.desktop-layout.dark) {
	background-color: var(--dark-bg-primary) !important;
	overflow: hidden;
}

body:has(.desktop-layout.dark) #body {
	background-color: var(--dark-bg-primary) !important;
}

body:has(.desktop-layout.light) {
	background-color: var(--background-color-main) !important;
	overflow: hidden;
}

body:has(.desktop-layout.light) #body {
	background-color: var(--background-color-main) !important;
}

</style>
